/**
 * KK-601 · Gelir ve gider kalemleri.
 *
 * Gider kategorileri sabittir (kira, personel, elektrik, mal alımı,
 * fire/imha, diğer) ve kâr-zarar raporunun kaynağıdır.
 *
 * Kategori kırılımı BİLEREK tablo olarak sunulur: altı satırlık bir dağılım
 * grafiğe çevrilince bilgi eklemez, gürültü ekler. Toplamlar Decimal ile
 * (@kantin/shared · paraTopla) hesaplanır; hiçbir aşamada number'a düşülmez.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  Buton,
  Kart,
  Secim,
  Tablo,
  TabloBaslik,
  TabloGovde,
  Tutar,
  Uyari,
} from "@kantin/ui";
import { IstatistikKarosu } from "@kantin/ui/grafik";
import { paraTopla } from "@kantin/shared";
import { apiOku, apiYaz } from "./api.js";
import { noktaSecenekleri, useNoktalar } from "./kancalar.js";
import {
  IslemDurumu,
  MetinAlani,
  Sayfa,
  SayfaBasligi,
  Sekmeler,
  SorguDurumu,
  TehlikeliOnay,
  ayBasi,
  bugun,
  paraGecerliMi,
  paraNormalize,
  tarihGoster,
} from "./ortak.js";

// ---------------------------------------------------------------------------

const GIDER_KATEGORI: { deger: string; metin: string }[] = [
  { deger: "1", metin: "Kira" },
  { deger: "2", metin: "Personel" },
  { deger: "3", metin: "Elektrik" },
  { deger: "4", metin: "Mal alımı" },
  { deger: "5", metin: "Fire / imha" },
  { deger: "6", metin: "Diğer" },
];

interface GiderKalemi {
  id: string;
  nokta_id: string | null;
  nokta_ad: string | null;
  kategori: number;
  kategori_ad: string;
  tutar: string;
  aciklama: string | null;
  belge_no: string | null;
  tarih: string;
  olusma_zamani: string;
}

interface GelirKalemi {
  id: string;
  nokta_id: string | null;
  nokta_ad: string | null;
  kategori: number;
  tutar: string;
  aciklama: string | null;
  tarih: string;
  olusma_zamani: string;
}

type Sekme = "gider" | "gelir";

export function GelirGider() {
  const [sekme, setSekme] = useState<Sekme>("gider");
  const [bas, setBas] = useState(ayBasi());
  const [bit, setBit] = useState(bugun());
  const [noktaId, setNoktaId] = useState("");
  const noktalar = useNoktalar();

  return (
    <Sayfa>
      <SayfaBasligi
        baslik="Gelir ve gider"
        aciklama="Kantin işletmesinin satış dışı gelir ve gider kalemleri. Kâr-zarar raporu bu kalemleri kullanır."
      />

      <Kart baslik="Dönem ve kapsam">
        <div className="grid gap-3 md:grid-cols-3">
          <Alan
            etiket="Başlangıç"
            type="date"
            value={bas}
            onChange={(e) => setBas(e.target.value)}
          />
          <Alan
            etiket="Bitiş"
            type="date"
            value={bit}
            onChange={(e) => setBit(e.target.value)}
          />
          <Secim
            etiket="Nokta"
            value={noktaId}
            onChange={(e) => setNoktaId(e.target.value)}
            secenekler={noktaSecenekleri(noktalar.data?.noktalar)}
          />
        </div>
      </Kart>

      <Sekmeler<Sekme>
        etiket="Kalem türleri"
        secili={sekme}
        sec={setSekme}
        sekmeler={[
          { deger: "gider", metin: "Gider" },
          { deger: "gelir", metin: "Gelir" },
        ]}
      />

      {sekme === "gider" ? (
        <Giderler bas={bas} bit={bit} noktaId={noktaId} />
      ) : (
        <Gelirler bas={bas} bit={bit} noktaId={noktaId} />
      )}
    </Sayfa>
  );
}

// ---------------------------------------------------------------------------

function Giderler({
  bas,
  bit,
  noktaId,
}: {
  bas: string;
  bit: string;
  noktaId: string;
}) {
  const istemci = useQueryClient();
  const [kategori, setKategori] = useState("");
  const [silinecek, setSilinecek] = useState<GiderKalemi | null>(null);

  const sorgu = useQuery({
    queryKey: ["gider", bas, bit, noktaId, kategori],
    queryFn: () =>
      apiOku<{ kalemler: GiderKalemi[] }>("/gider", {
        bas,
        bit,
        nokta_id: noktaId || undefined,
        kategori: kategori || undefined,
        limit: 500,
      }),
  });

  const sil = useMutation({
    mutationFn: (id: string) => apiYaz("DELETE", `/gider/${id}`),
    onSuccess: () => {
      setSilinecek(null);
      void istemci.invalidateQueries({ queryKey: ["gider"] });
    },
  });

  const kalemler = sorgu.data?.kalemler ?? [];
  const toplam = paraTopla(...kalemler.map((k) => k.tutar)).toFixed(2);

  // Kategori kırılımı — Decimal toplamlar, string olarak tutulur.
  const kirilim = GIDER_KATEGORI.map((k) => {
    const kod = Number.parseInt(k.deger, 10);
    const iliskili = kalemler.filter((x) => x.kategori === kod);
    return {
      kod,
      ad: k.metin,
      adet: iliskili.length,
      tutar: paraTopla(...iliskili.map((x) => x.tutar)).toFixed(2),
    };
  }).filter((k) => k.adet > 0);

  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <IstatistikKarosu
          etiket="Dönem gider toplamı"
          deger={
            <Tutar deger={toplam} yonsuz boy="2xl" etiket="gider toplamı" />
          }
          yardimci={`${kalemler.length} kalem`}
        />
        <IstatistikKarosu
          etiket="Kategori sayısı"
          deger={<span className="rakam font-mono">{kirilim.length}</span>}
        />
      </div>

      <Kart baslik="Kategori kırılımı">
        <SorguDurumu
          yukleniyor={sorgu.isPending}
          hata={sorgu.error}
          bos={kirilim.length === 0}
          bosBaslik="Bu dönemde gider yok"
          bosAciklama="Tarih aralığını genişletin veya aşağıdan yeni gider kalemi ekleyin."
          yenile={() => void sorgu.refetch()}
        >
          <Tablo>
            <TabloBaslik>
              <th scope="col">Kategori</th>
              <th scope="col">Kalem</th>
              <th scope="col">Tutar</th>
            </TabloBaslik>
            <TabloGovde>
              {kirilim.map((k) => (
                <tr key={k.kod}>
                  <th scope="row" className="px-3 py-2 text-left font-semibold">
                    {k.ad}
                  </th>
                  <td className="rakam text-right font-mono">{k.adet}</td>
                  <td className="text-right">
                    <Tutar deger={k.tutar} yonsuz etiket={`${k.ad} toplamı`} />
                  </td>
                </tr>
              ))}
            </TabloGovde>
          </Tablo>
        </SorguDurumu>
      </Kart>

      <Kart
        baslik="Gider kalemleri"
        ustBilgi={
          <div className="w-48">
            <Secim
              etiket="Kategori süzgeci"
              value={kategori}
              onChange={(e) => setKategori(e.target.value)}
              secenekler={[
                { deger: "", metin: "Tüm kategoriler" },
                ...GIDER_KATEGORI,
              ]}
            />
          </div>
        }
      >
        <SorguDurumu
          yukleniyor={sorgu.isPending}
          hata={sorgu.error}
          bos={kalemler.length === 0}
          bosBaslik="Gider kalemi yok"
          bosAciklama="Bu dönem ve süzgeçte gider bulunmuyor."
          yenile={() => void sorgu.refetch()}
        >
          <Tablo>
            <TabloBaslik>
              <th scope="col">Tarih</th>
              <th scope="col">Kategori</th>
              <th scope="col">Nokta</th>
              <th scope="col">Belge</th>
              <th scope="col">Açıklama</th>
              <th scope="col">Tutar</th>
              <th scope="col">
                <span className="gizli-erisilebilir">İşlem</span>
              </th>
            </TabloBaslik>
            <TabloGovde>
              {kalemler.map((k) => (
                <tr key={k.id}>
                  <td className="whitespace-nowrap text-sm">
                    {tarihGoster(k.tarih)}
                  </td>
                  <td className="text-sm text-murekkep-2">{k.kategori_ad}</td>
                  <td className="text-sm text-murekkep-2">
                    {k.nokta_ad ?? "Okul geneli"}
                  </td>
                  <td className="rakam font-mono text-xs">
                    {k.belge_no ?? "—"}
                  </td>
                  <td className="text-sm text-murekkep-2">
                    {k.aciklama ?? "—"}
                  </td>
                  <td className="text-right">
                    {/* Gider defterde KIRMIZI mürekkeptir: para çıkan. */}
                    <Tutar deger={`-${k.tutar}`} etiket="gider" />
                  </td>
                  <td className="text-right">
                    <Buton tur="sessiz" onClick={() => setSilinecek(k)}>
                      Sil
                    </Buton>
                  </td>
                </tr>
              ))}
            </TabloGovde>
          </Tablo>
        </SorguDurumu>
      </Kart>

      {silinecek && (
        <TehlikeliOnay
          soru="Bu gider kalemi silinsin mi?"
          aciklama={
            <>
              <p className="m-0">
                {tarihGoster(silinecek.tarih)} · {silinecek.kategori_ad} ·{" "}
                <Tutar deger={silinecek.tutar} yonsuz boy="sm" />
              </p>
              <p className="m-0 mt-1">
                Silinen kaydın tam içeriği denetim kaydına yazılır; kâr-zarar
                raporu bu değişiklikten sonra farklı sonuç verir.
              </p>
            </>
          }
          onayMetni="Kalemi sil"
          onayla={() => sil.mutate(silinecek.id)}
          vazgec={() => setSilinecek(null)}
          yukleniyor={sil.isPending}
          cocuk={<IslemDurumu hata={sil.error} />}
        />
      )}

      <GiderFormu
        varsayilanNokta={noktaId}
        kaydedildi={() =>
          void istemci.invalidateQueries({ queryKey: ["gider"] })
        }
      />
    </>
  );
}

function GiderFormu({
  varsayilanNokta,
  kaydedildi,
}: {
  varsayilanNokta: string;
  kaydedildi: () => void;
}) {
  const noktalar = useNoktalar();
  const [kategori, setKategori] = useState("6");
  const [tutar, setTutar] = useState("");
  const [tarih, setTarih] = useState(bugun());
  const [noktaId, setNoktaId] = useState(varsayilanNokta);
  const [belgeNo, setBelgeNo] = useState("");
  const [aciklama, setAciklama] = useState("");
  const [gonderildi, setGonderildi] = useState(false);

  const kaydet = useMutation({
    mutationFn: () =>
      apiYaz<{ id: string }>("POST", "/gider", {
        nokta_id: noktaId || null,
        kategori: Number.parseInt(kategori, 10),
        tutar: paraNormalize(tutar),
        aciklama: aciklama.trim() || null,
        belge_no: belgeNo.trim() || null,
        tarih,
      }),
    onSuccess: () => {
      setTutar("");
      setBelgeNo("");
      setAciklama("");
      setGonderildi(false);
      kaydedildi();
    },
  });

  const tutarHatasi =
    gonderildi && !paraGecerliMi(tutar)
      ? "Tutarı 1250,00 biçiminde ve sıfırdan büyük girin."
      : undefined;

  return (
    <Kart baslik="Yeni gider kalemi">
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setGonderildi(true);
          if (!paraGecerliMi(tutar)) return;
          kaydet.mutate();
        }}
      >
        <div className="grid gap-3 md:grid-cols-4">
          <Secim
            etiket="Kategori"
            value={kategori}
            onChange={(e) => setKategori(e.target.value)}
            secenekler={GIDER_KATEGORI}
          />
          <Alan
            etiket="Tutar"
            value={tutar}
            onChange={(e) => setTutar(e.target.value)}
            inputMode="decimal"
            {...(tutarHatasi ? { hata: tutarHatasi } : {})}
          />
          <Alan
            etiket="Tarih"
            type="date"
            value={tarih}
            onChange={(e) => setTarih(e.target.value)}
          />
          <Secim
            etiket="Nokta"
            value={noktaId}
            onChange={(e) => setNoktaId(e.target.value)}
            secenekler={noktaSecenekleri(
              noktalar.data?.noktalar,
              "Okul geneli",
            )}
          />
          <Alan
            etiket="Belge no"
            value={belgeNo}
            onChange={(e) => setBelgeNo(e.target.value)}
          />
        </div>
        <MetinAlani
          etiket="Açıklama"
          value={aciklama}
          onChange={(e) => setAciklama(e.target.value)}
          rows={2}
        />
        <IslemDurumu hata={kaydet.error} />
        <div>
          <Buton type="submit" tur="birincil" yukleniyor={kaydet.isPending}>
            Gideri kaydet
          </Buton>
        </div>
      </form>
    </Kart>
  );
}

// ---------------------------------------------------------------------------

function Gelirler({
  bas,
  bit,
  noktaId,
}: {
  bas: string;
  bit: string;
  noktaId: string;
}) {
  const istemci = useQueryClient();
  const [silinecek, setSilinecek] = useState<GelirKalemi | null>(null);

  const sorgu = useQuery({
    queryKey: ["gelir", bas, bit, noktaId],
    queryFn: () =>
      apiOku<{ kalemler: GelirKalemi[]; toplam: string }>("/gelir", {
        bas,
        bit,
        nokta_id: noktaId || undefined,
        limit: 500,
      }),
  });

  const sil = useMutation({
    mutationFn: (id: string) => apiYaz("DELETE", `/gelir/${id}`),
    onSuccess: () => {
      setSilinecek(null);
      void istemci.invalidateQueries({ queryKey: ["gelir"] });
    },
  });

  const kalemler = sorgu.data?.kalemler ?? [];

  return (
    <>
      <Uyari tur="bilgi" baslik="Satış cirosu buraya girilmez">
        Bu ekran kantin satışları dışındaki gelirler içindir (kira geliri,
        bağış, etkinlik geliri gibi). Satış cirosu satış kayıtlarından gelir ve
        raporlarda ayrı gösterilir.
      </Uyari>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <IstatistikKarosu
          etiket="Dönem gelir toplamı"
          deger={
            <Tutar
              deger={sorgu.data?.toplam ?? "0.00"}
              yonsuz
              boy="2xl"
              etiket="gelir toplamı"
            />
          }
          yardimci={`${kalemler.length} kalem`}
        />
      </div>

      <Kart baslik="Gelir kalemleri">
        <SorguDurumu
          yukleniyor={sorgu.isPending}
          hata={sorgu.error}
          bos={kalemler.length === 0}
          bosBaslik="Gelir kalemi yok"
          bosAciklama="Bu dönemde satış dışı gelir kaydı bulunmuyor."
          yenile={() => void sorgu.refetch()}
        >
          <Tablo>
            <TabloBaslik>
              <th scope="col">Tarih</th>
              <th scope="col">Nokta</th>
              <th scope="col">Açıklama</th>
              <th scope="col">Tutar</th>
              <th scope="col">
                <span className="gizli-erisilebilir">İşlem</span>
              </th>
            </TabloBaslik>
            <TabloGovde>
              {kalemler.map((k) => (
                <tr key={k.id}>
                  <td className="whitespace-nowrap text-sm">
                    {tarihGoster(k.tarih)}
                  </td>
                  <td className="text-sm text-murekkep-2">
                    {k.nokta_ad ?? "Okul geneli"}
                  </td>
                  <td className="text-sm text-murekkep-2">
                    {k.aciklama ?? "—"}
                  </td>
                  <td className="text-right">
                    {/* Gelir defterde SİYAH mürekkeptir: para giren. */}
                    <Tutar deger={k.tutar} isaretiGoster etiket="gelir" />
                  </td>
                  <td className="text-right">
                    <Buton tur="sessiz" onClick={() => setSilinecek(k)}>
                      Sil
                    </Buton>
                  </td>
                </tr>
              ))}
            </TabloGovde>
          </Tablo>
        </SorguDurumu>
      </Kart>

      {silinecek && (
        <TehlikeliOnay
          soru="Bu gelir kalemi silinsin mi?"
          aciklama={
            <p className="m-0">
              {tarihGoster(silinecek.tarih)} ·{" "}
              <Tutar deger={silinecek.tutar} yonsuz boy="sm" /> · Silinen kaydın
              tam içeriği denetim kaydına yazılır.
            </p>
          }
          onayMetni="Kalemi sil"
          onayla={() => sil.mutate(silinecek.id)}
          vazgec={() => setSilinecek(null)}
          yukleniyor={sil.isPending}
          cocuk={<IslemDurumu hata={sil.error} />}
        />
      )}

      <GelirFormu
        varsayilanNokta={noktaId}
        kaydedildi={() =>
          void istemci.invalidateQueries({ queryKey: ["gelir"] })
        }
      />
    </>
  );
}

function GelirFormu({
  varsayilanNokta,
  kaydedildi,
}: {
  varsayilanNokta: string;
  kaydedildi: () => void;
}) {
  const noktalar = useNoktalar();
  const [tutar, setTutar] = useState("");
  const [tarih, setTarih] = useState(bugun());
  const [noktaId, setNoktaId] = useState(varsayilanNokta);
  const [aciklama, setAciklama] = useState("");
  const [gonderildi, setGonderildi] = useState(false);

  const kaydet = useMutation({
    mutationFn: () =>
      apiYaz<{ id: string }>("POST", "/gelir", {
        nokta_id: noktaId || null,
        tutar: paraNormalize(tutar),
        aciklama: aciklama.trim() || null,
        tarih,
      }),
    onSuccess: () => {
      setTutar("");
      setAciklama("");
      setGonderildi(false);
      kaydedildi();
    },
  });

  const tutarHatasi =
    gonderildi && !paraGecerliMi(tutar)
      ? "Tutarı 1250,00 biçiminde ve sıfırdan büyük girin."
      : undefined;

  return (
    <Kart baslik="Yeni gelir kalemi">
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setGonderildi(true);
          if (!paraGecerliMi(tutar)) return;
          kaydet.mutate();
        }}
      >
        <div className="grid gap-3 md:grid-cols-3">
          <Alan
            etiket="Tutar"
            value={tutar}
            onChange={(e) => setTutar(e.target.value)}
            inputMode="decimal"
            {...(tutarHatasi ? { hata: tutarHatasi } : {})}
          />
          <Alan
            etiket="Tarih"
            type="date"
            value={tarih}
            onChange={(e) => setTarih(e.target.value)}
          />
          <Secim
            etiket="Nokta"
            value={noktaId}
            onChange={(e) => setNoktaId(e.target.value)}
            secenekler={noktaSecenekleri(
              noktalar.data?.noktalar,
              "Okul geneli",
            )}
          />
        </div>
        <MetinAlani
          etiket="Açıklama"
          value={aciklama}
          onChange={(e) => setAciklama(e.target.value)}
          rows={2}
        />
        <IslemDurumu hata={kaydet.error} />
        <div>
          <Buton type="submit" tur="birincil" yukleniyor={kaydet.isPending}>
            Geliri kaydet
          </Buton>
        </div>
      </form>
    </Kart>
  );
}
